import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, MessageCircle, Phone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

import { COMPANY, PRODUCT_CATEGORIES } from "@/mock";
import { postRfqLead } from "@/lib/api";
import { Container, makeMailUrl, makeTelUrl, makeWhatsAppUrl, setTitle } from "@/pages/_shared";

export default function RequestQuotePage({ locationState }) {
  useEffect(() => setTitle("Request a Quote"), []);

  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    product: locationState?.product || "",
    spec: "",
    quantity: "",
    deliveryLocation: "",
  });

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.product || !form.quantity) {
      toast.error("Please fill Name, Phone, Product and Quantity.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        company: form.company || undefined,
        phone: form.phone,
        email: form.email || undefined,
        product: form.product,
        quantity: form.quantity,
        spec: form.spec || undefined,
        delivery_location: form.deliveryLocation || undefined,
        source_page: "/request-quote",
        message: "RFQ submitted via website form",
      };

      const created = await postRfqLead(payload);
      toast.success("RFQ submitted", {
        description: `Saved successfully. Reference: ${created.id}`,
      });
      setForm({
        name: "",
        company: "",
        phone: "",
        email: "",
        product: "",
        spec: "",
        quantity: "",
        deliveryLocation: "",
      });
    } catch (err) {
      toast.error("Could not submit RFQ", {
        description: "Please try again or WhatsApp us. (Backend connection failed)",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="py-14">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Request a Quote</h1>
              <p className="mt-4 text-base text-muted-foreground max-w-2xl">
                Share your requirement—our team will respond with pricing, lead time and feasible specifications.
              </p>

              <form className="mt-8 rounded-3xl border border-border/60 bg-background/70 p-6 grid gap-4" onSubmit={onSubmit}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold">Name *</label>
                    <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold">Company / Department</label>
                    <Input value={form.company} onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))} placeholder="Company" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold">Phone *</label>
                    <Input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} placeholder="Phone number" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold">Email</label>
                    <Input value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} placeholder="Email" />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold">Product *</label>
                    <Input value={form.product} onChange={(e) => setForm((s) => ({ ...s, product: e.target.value }))} placeholder="e.g., GI Wire / Chainlink Fence / Gabion Box" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold">Quantity *</label>
                    <Input value={form.quantity} onChange={(e) => setForm((s) => ({ ...s, quantity: e.target.value }))} placeholder="e.g., 10 MT / 500 rolls" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold">Specification (diameter/mesh/coating/standard)</label>
                  <Textarea value={form.spec} onChange={(e) => setForm((s) => ({ ...s, spec: e.target.value }))} placeholder="Diameter/mesh, GI/PVC, IS/ASTM grade, roll size, packaging…" />
                </div>

                <div>
                  <label className="text-xs font-semibold">Delivery Location</label>
                  <Input value={form.deliveryLocation} onChange={(e) => setForm((s) => ({ ...s, deliveryLocation: e.target.value }))} placeholder="City/State (e.g., Guwahati, Assam)" />
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button className="bg-amber-500 text-slate-950 hover:bg-amber-400" type="submit" disabled={submitting}>
                    {submitting ? "Submitting…" : "Submit RFQ"} {!submitting ? <ArrowRight className="ml-2 h-4 w-4" /> : null}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
                    onClick={() => {
                      const text = `Hello, I need a quotation.\nProduct: ${form.product || "__"}\nQty: ${form.quantity || "__"}\nSpec: ${form.spec || "__"}\nLocation: ${form.deliveryLocation || "__"}`;
                      window.open(makeWhatsAppUrl(COMPANY.whatsapp, text), "_blank");
                    }}
                  >
                    <MessageCircle className="mr-2 h-4 w-4" /> Send via WhatsApp
                  </Button>
                  <Button type="button" variant="outline" onClick={() => (window.location.href = makeTelUrl(COMPANY.phone))}>
                    <Phone className="mr-2 h-4 w-4" /> Call Now
                  </Button>
                </div>

                <div className="text-xs text-muted-foreground">
                  RFQs submitted here are saved for follow-up. For urgent pricing, use WhatsApp for faster response.
                </div>
              </form>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-border/60 bg-slate-950 p-8 text-slate-50">
                <div className="text-xs font-bold tracking-widest text-emerald-200 uppercase">What to include</div>
                <h2 className="mt-3 text-2xl font-extrabold tracking-tight">Faster quote if you share:</h2>
                <ul className="mt-4 space-y-2 text-sm text-slate-200">
                  <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400" /> Product + variant</li>
                  <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400" /> Diameter/mesh/aperture</li>
                  <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400" /> GI/PVC coating requirement</li>
                  <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400" /> Standard (IS/ASTM) if required</li>
                  <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400" /> Quantity + location</li>
                </ul>

                <Separator className="my-6 bg-white/10" />

                <div className="text-sm font-extrabold tracking-tight">Direct contact</div>
                <div className="mt-3 grid gap-2 text-sm text-slate-200">
                  <a className="inline-flex items-center gap-2 hover:text-white" href={makeTelUrl(COMPANY.phone)}>
                    <Phone className="h-4 w-4" /> {COMPANY.phone}
                  </a>
                  <a
                    className="inline-flex items-center gap-2 hover:text-white"
                    href={makeWhatsAppUrl(COMPANY.whatsapp, "Hello, I want a quotation.")}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MessageCircle className="h-4 w-4" /> {COMPANY.whatsapp}
                  </a>
                  <a className="inline-flex items-center gap-2 hover:text-white" href={makeMailUrl(COMPANY.email)}>
                    <Mail className="h-4 w-4" /> {COMPANY.email}
                  </a>
                </div>
              </div>

              <div className="mt-6 rounded-3xl border border-border/60 bg-secondary/25 p-6">
                <div className="text-sm font-extrabold tracking-tight">Popular categories</div>
                <div className="mt-3 grid gap-2">
                  {PRODUCT_CATEGORIES.map((c) => (
                    <Link
                      key={c.id}
                      className="rounded-xl border border-border/60 bg-background/70 p-3 text-sm font-semibold text-muted-foreground hover:text-foreground"
                      to={`/products/${c.id}`}
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
                <Separator className="my-4" />
                <Button
                  className="w-full bg-slate-900 text-slate-50 hover:bg-slate-800"
                  onClick={() => navigate("/products")}
                >
                  Browse all products
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
